import { Popover } from '@mantine/core';
import { useCallback, useRef, useState } from 'react';

interface PopoverMenuItemProps {
	children: React.ReactNode;
	target?: React.ReactNode;
}

function PopoverMenuItem({ children, target }: PopoverMenuItemProps) {
	const [targetFocused, setTargetFocused] = useState(false);
	const [dropdownFocused, setDropdownFocused] = useState(false);
	const dropdownRef = useRef<HTMLDivElement>(null);

	const close = useCallback(() => {
		setTargetFocused(false);
		setDropdownFocused(false);
	}, []);

	const handleTargetEnter = useCallback(() => {
		setTargetFocused(true);
	}, []);

	const handleTargetLeave = useCallback(() => {
		setTimeout(() => setTargetFocused(false), 200);
	}, []);

	const handleDropdownEnter = useCallback(() => {
		setDropdownFocused(true);
	}, []);

	const handleDropdownBlur = useCallback(
		(event: React.FocusEvent<HTMLDivElement>) => {
			// Do not close when focus moves to another element inside the dropdown (e.g. from
			// the search input to a result item on mousedown). Otherwise click-and-hold closes
			// the popover before the click fires and no action runs.
			const relatedTarget = event.relatedTarget as Node | null;
			if (dropdownRef.current?.contains(relatedTarget)) {
				return;
			}
			setDropdownFocused(false);
		},
		[]
	);

	return (
		<Popover
			opened={targetFocused || dropdownFocused}
			onClose={close}
			width={280}
			position="right-start"
			radius={'md'}
			shadow="xl"
		>
			<Popover.Target>
				<div onMouseEnter={handleTargetEnter} onMouseLeave={handleTargetLeave}>
					{target}
				</div>
			</Popover.Target>

			<Popover.Dropdown>
				<div
					ref={dropdownRef}
					onMouseEnter={handleDropdownEnter}
					onBlurCapture={handleDropdownBlur}
				>
					{children}
				</div>
			</Popover.Dropdown>
		</Popover>
	);
}

export default PopoverMenuItem;
